import { orderTokenRequestOptions } from '@/utils/global/orderTokenHeaders'
import {
  createOrderSchema,
  updateOrderSchema,
  getOrderSchema,
  getFuneralHomeOrdersSchema,
  generatePDFSchema,
  editorBackButtonRequestSchema,
  duplicateOrderSchema,
} from '@messenger/types'
import type {
  GetFuneralHomeOrderNormalized,
  GetOrderNormalized,
  GetM3OrderWithProductCaseNormalized,
  GetM3OrderNormalized,
  GetGeneratedPdf,
  GetPdfStatus,
  CheckProofPDFResponse,
} from '@messenger/types'
import { fetchClient as fetch } from '@/api/fetch.config'
import axios from '@/api/axios.config'
import { z } from 'zod'
import sanitizeDashboardDates from '@/utils/sanitizers/sanitizeDashboardDates'

// note, this endpoint IS used, but IDE will have trouble finding it
// see DashboardTable.js for current usage
export const getFuneralHomeOrders = async (
  archived: z.infer<typeof getFuneralHomeOrdersSchema>['archived'],
) => {
  const response = await axios.get<GetFuneralHomeOrderNormalized>(
    `/funeralHomeOrders?archived=${archived}`,
  )

  response.data.orders = sanitizeDashboardDates(
    response.data.orders,
  ) as GetFuneralHomeOrderNormalized['orders']
  return response
}

export const createOrder = async (
  payload: z.infer<typeof createOrderSchema>,
) => {
  return await axios.post<GetOrderNormalized>(`createOrder`, payload)
}

export const updateOrder = async (
  orderId: string,
  payload: z.infer<typeof updateOrderSchema>,
) => {
  return await axios.patch<GetOrderNormalized>(`orders/${orderId}`, payload)
}

export const getOrder = async (
  orderId: string,
  params: Partial<z.infer<typeof getOrderSchema>> = {},
) => {
  const queryString = new URLSearchParams(
    Object.entries(params).reduce(
      (acc, [key, value]) => {
        if (value !== undefined && value !== null) {
          acc[key] = String(value)
        }
        return acc
      },
      {} as Record<string, string>,
    ),
  ).toString()
  const url = queryString
    ? `orders/${orderId}?${queryString}`
    : `orders/${orderId}`
  return await axios.get<
    | GetOrderNormalized
    | GetM3OrderWithProductCaseNormalized
    | GetM3OrderNormalized
  >(url)
}

export const checkProofPDF = async (
  orderId: string,
  productId: string,
): Promise<CheckProofPDFResponse> => {
  const options = orderTokenRequestOptions()

  return (await fetch.get(
    `checkProofPDF/${orderId}/${productId}`,
    undefined,
    options,
  )) as CheckProofPDFResponse
}

export const generatePDF = async (
  payload: z.infer<typeof generatePDFSchema>,
) => {
  const options = orderTokenRequestOptions()

  return (await fetch.post(`generatePDF`, payload, options)) as GetGeneratedPdf
}

export const generatePDFStatus = async (pdfId: string) => {
  const options = orderTokenRequestOptions()

  return (await fetch.get(
    `generatePDFStatus/${pdfId}`,
    undefined,
    options,
  )) as GetPdfStatus
}

export type ScreenshotStatusResponse = {
  status: 'pending' | 'processing' | 'complete' | 'error' | null
  screenshotUrls: string[]
  galleryToken?: string | null
  /** Why the job failed, on an answer whose status is `error`. */
  reason?: string
}

export const getScreenshotStatus = async (
  pdfId: string,
): Promise<ScreenshotStatusResponse> => {
  const options = orderTokenRequestOptions()

  return (await fetch.get(
    `screenshotStatus/${pdfId}`,
    undefined,
    options,
  )) as ScreenshotStatusResponse
}

/**
 * A proof whose screenshot job failed. `reason` is the status route's answer,
 * kept for logs; the editor shows its own translated message.
 */
export class ScreenshotJobFailedError extends Error {
  readonly reason: string | undefined

  constructor(reason: string | undefined) {
    super(reason ?? 'The screenshot job failed')
    this.name = 'ScreenshotJobFailedError'
    this.reason = reason
  }
}

/**
 * Polls a screenshot proof's status every `intervalMs` until its job has
 * written its screenshots, and resolves to that answer; resolves to null
 * when `maxAttempts` reads came back without them. An answer that says the
 * job failed ends the poll at once, with a ScreenshotJobFailedError carrying
 * the job's reason.
 * A read that failed (the server answered with an error, or nothing
 * answered) says nothing about the job, so it is read again.
 */
export const pollScreenshotProofStatus = async (
  pdfId: string,
  // 90 × 2000ms = 180s ceiling: covers the #2551 paint warmup (fullPage
  // screenshot pass) plus worker contention during peak proof-generation
  // queues.
  maxAttempts = 90,
  intervalMs = 2000,
): Promise<ScreenshotStatusResponse | null> => {
  let attempts = 0
  while (attempts < maxAttempts) {
    let status: ScreenshotStatusResponse | undefined
    try {
      status = await getScreenshotStatus(pdfId)
    } catch (error) {
      console.error('Error polling screenshot status', { pdfId, error })
    }
    if (status?.status === 'complete' && status.screenshotUrls.length > 0) {
      return status
    }
    if (status?.status === 'error') {
      console.error('Screenshot generation failed', {
        pdfId,
        reason: status.reason,
      })
      throw new ScreenshotJobFailedError(status.reason)
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs))
    attempts++
  }
  console.warn('Screenshot polling timed out', { pdfId, attempts })
  return null
}

export const notifyEditorBackButton = async (
  payload: z.infer<typeof editorBackButtonRequestSchema>,
) => {
  const options = orderTokenRequestOptions()

  return (await fetch.post(`editorBackButton`, payload, options)) as {
    success: boolean
    message: string
    action: 'notified' | 'skipped'
  }
}

export const duplicateOrder = async (
  payload: z.infer<typeof duplicateOrderSchema>,
) => {
  return await axios.post<GetOrderNormalized>(`duplicateOrder`, payload)
}

// Note: these endpoints are used, but IDE will have trouble finding them
export const archiveOrder = async (orderId: string) => {
  return await axios.patch<GetOrderNormalized>(`archiveOrder/${orderId}`)
}

export const unarchiveOrder = async (orderId: string) => {
  return await axios.patch<GetOrderNormalized>(`unarchiveOrder/${orderId}`)
}

export const validateEditToken = async (token: string) => {
  return await axios.get<{
    orderId?: string
    m3OrderId?: string
    isM3?: boolean
    valid: boolean
    error?: boolean
    message?: string
    returnUrl?: string
    // The token-bound editor access level and optional single-product
    // scope — the editor's auth level comes from here, never the URL.
    role?: 'FH_ADMIN' | 'DESIGNER'
    scopeOrderProductId?: string | null
  }>(`validateEditToken/${token}`)
}
